// ── Zone geometry: point-in-polygon, spatial index ──────────────────────────
// Pure functions, no imports beyond types. Every live position and every Trip
// stop location passes through zonesAtPoint/pointInZone, so they must stay
// O(zones near this point), never O(all zones).

import type { PreparedZone } from './zone-activity-types';

/** Grid cell edge in metres. ~1km keeps a typical city's zones a few per cell. */
export const CELL_M = 1000;

/**
 * A zone whose bounding box covers more cells than this is kept in a linear
 * list instead of being stamped into every cell. Guards memory against the odd
 * state-sized or misdrawn zone without penalising the common case.
 */
export const OVERSIZE_CELLS = 2048;

const M_PER_DEG_LAT = 111_320;

export interface ZoneGrid {
  zones: PreparedZone[];
  latStep: number;
  lngStep: number;
  /** cell key → indices into `zones` */
  cells: Map<string, number[]>;
  /** indices of zones too large to stamp into cells; always candidates */
  oversized: number[];
}

function cellKey(latIdx: number, lngIdx: number): string {
  return latIdx + ':' + lngIdx;
}

/**
 * Index zones by the cells their bounding boxes overlap. Because insertion is
 * by bbox coverage (not just the centroid), a point lookup only needs its own
 * cell — no 3x3 neighbourhood scan.
 */
export function buildZoneGrid(zones: PreparedZone[]): ZoneGrid {
  const latStep = CELL_M / M_PER_DEG_LAT;

  // Longitude degrees shrink toward the poles. Size the cell from the mean
  // latitude of the zone set so cells stay roughly square for this database.
  let latSum = 0;
  for (const z of zones) latSum += (z.minLat + z.maxLat) / 2;
  const meanLat = zones.length ? latSum / zones.length : 0;
  const cosLat  = Math.max(0.05, Math.cos((meanLat * Math.PI) / 180));
  const lngStep = CELL_M / (M_PER_DEG_LAT * cosLat);

  const cells     = new Map<string, number[]>();
  const oversized: number[] = [];

  zones.forEach((z, idx) => {
    const lat0 = Math.floor(z.minLat / latStep);
    const lat1 = Math.floor(z.maxLat / latStep);
    const lng0 = Math.floor(z.minLng / lngStep);
    const lng1 = Math.floor(z.maxLng / lngStep);
    const span = (lat1 - lat0 + 1) * (lng1 - lng0 + 1);

    if (span > OVERSIZE_CELLS) {
      oversized.push(idx);
      return;
    }
    for (let a = lat0; a <= lat1; a++) {
      for (let b = lng0; b <= lng1; b++) {
        const key = cellKey(a, b);
        const bucket = cells.get(key);
        if (bucket) bucket.push(idx);
        else cells.set(key, [idx]);
      }
    }
  });

  return { zones, latStep, lngStep, cells, oversized };
}

/** Candidate zone indices whose bbox contains the point. Cheap pre-filter for PIP. */
export function zonesAtPoint(grid: ZoneGrid, lat: number, lng: number): number[] {
  const bucket = grid.cells.get(
    cellKey(Math.floor(lat / grid.latStep), Math.floor(lng / grid.lngStep)),
  );
  if (!bucket && !grid.oversized.length) return [];

  const out: number[] = [];
  const consider = (idx: number): void => {
    const z = grid.zones[idx];
    if (lat >= z.minLat && lat <= z.maxLat && lng >= z.minLng && lng <= z.maxLng) out.push(idx);
  };
  if (bucket) bucket.forEach(consider);
  grid.oversized.forEach(consider);
  return out;
}

/**
 * Even-odd ray cast. The closing edge is implicit (the ring arrays never repeat
 * the first point). Self-intersecting rings still resolve deterministically.
 */
export function pointInZone(pLat: number, pLng: number, z: PreparedZone): boolean {
  if (pLat < z.minLat || pLat > z.maxLat || pLng < z.minLng || pLng > z.maxLng) return false;
  const { lat, lng } = z;
  const n = lat.length;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const yi = lat[i], xi = lng[i];
    const yj = lat[j], xj = lng[j];
    if ((yi > pLat) !== (yj > pLat)) {
      const xCross = ((xj - xi) * (pLat - yi)) / (yj - yi) + xi;
      if (pLng < xCross) inside = !inside;
    }
  }
  return inside;
}
